import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import TradingBlock from "../components/TradingBlock";
import "./PairAnalysisPage.css";
import InfoBlock from "../components/InfoBlock";

// Биржевые хуки — существующие
import { useMexcSpot } from "../hooks/orderbooks/useMexcSpot";
import { useMexcFutures } from "../hooks/orderbooks/useMexcFutures";
import { useGateSpot } from "../hooks/orderbooks/useGateSpot";
import { useGateFutures } from "../hooks/orderbooks/useGateFutures";
import { useBingxSpot } from "../hooks/orderbooks/useBingxSpot";
import { useBingxFutures } from "../hooks/orderbooks/useBingxFutures";
import { useBitgetSpot } from "../hooks/orderbooks/useBitgetSpot";
import { useBitgetFutures } from "../hooks/orderbooks/useBitgetFutures";
import { useBybitSpot } from "../hooks/orderbooks/useBybitSpot";
import { useBybitFutures } from "../hooks/orderbooks/useBybitFutures";
import { useKucoinSpot } from "../hooks/orderbooks/useKucoinSpot";
import { useKucoinFutures } from "../hooks/orderbooks/useKucoinFutures";
import { useLbankSpot } from "../hooks/orderbooks/useLbankSpot";
import { useLbankFutures } from "../hooks/orderbooks/useLbankFutures";
import { useBinanceSpot } from "../hooks/orderbooks/useBinanceSpot";
import { useBinanceFutures } from "../hooks/orderbooks/useBinanceFutures";
import { useOurbitSpot } from "../hooks/orderbooks/useOurbitSpot";
import { useOurbitFutures } from "../hooks/orderbooks/useOurbitFutures";

import SpreadChart from "../components/SpreadChart";

/** Универсальный слой данных. */
export const useExchangeData = (exchange, type, symbol) => {
  const mexcSpotData       = useMexcSpot(        exchange === "mexc"    && type === "spot"    ? symbol : null);
  const mexcFuturesData    = useMexcFutures(     exchange === "mexc"    && type === "futures" ? symbol : null);
  const gateSpotData       = useGateSpot(        exchange === "gate"    && type === "spot"    ? symbol : null);
  const gateFuturesData    = useGateFutures(     exchange === "gate"    && type === "futures" ? symbol : null);
  const bingxSpotData      = useBingxSpot(       exchange === "bingx"   && type === "spot"    ? symbol : null);
  const bingxFuturesData   = useBingxFutures(    exchange === "bingx"   && type === "futures" ? symbol : null);
  const bitgetSpotData     = useBitgetSpot(      exchange === "bitget"  && type === "spot"    ? symbol : null);
  const bitgetFuturesData  = useBitgetFutures(   exchange === "bitget"  && type === "futures" ? symbol : null);
  const bybitSpotData      = useBybitSpot(       exchange === "bybit"   && type === "spot"    ? symbol : null);
  const bybitFuturesData   = useBybitFutures(    exchange === "bybit"   && type === "futures" ? symbol : null);
  const kucoinSpotData     = useKucoinSpot(      exchange === "kucoin"  && type === "spot"    ? symbol : null);
  const kucoinFuturesData  = useKucoinFutures(   exchange === "kucoin"  && type === "futures" ? symbol : null);
  const lbankSpotData      = useLbankSpot(       exchange === "lbank"   && type === "spot"    ? symbol : null);
  const lbankFuturesData   = useLbankFutures(    exchange === "lbank"   && type === "futures" ? symbol : null);
  const binanceSpotData    = useBinanceSpot(     exchange === "binance" && type === "spot"    ? symbol : null);
  const binanceFuturesData = useBinanceFutures(  exchange === "binance" && type === "futures" ? symbol : null);
  const ourbitSpotData     = useOurbitSpot(     exchange === "ourbit"  && type === "spot"    ? symbol : null);
  const ourbitFuturesData  = useOurbitFutures(  exchange === "ourbit"  && type === "futures" ? symbol : null);
  if (exchange === "mexc"    && type === "spot")    return mexcSpotData;
  if (exchange === "mexc"    && type === "futures") return mexcFuturesData;
  if (exchange === "gate"    && type === "spot")    return gateSpotData;
  if (exchange === "gate"    && type === "futures") return gateFuturesData;
  if (exchange === "bingx"   && type === "spot")    return bingxSpotData;
  if (exchange === "bingx"   && type === "futures") return bingxFuturesData;
  if (exchange === "bitget"  && type === "spot")    return bitgetSpotData;
  if (exchange === "bitget"  && type === "futures") return bitgetFuturesData;
  if (exchange === "bybit"   && type === "spot")    return bybitSpotData;
  if (exchange === "bybit"   && type === "futures") return bybitFuturesData;
  if (exchange === "kucoin"  && type === "spot")    return kucoinSpotData;
  if (exchange === "kucoin"  && type === "futures") return kucoinFuturesData;
  if (exchange === "lbank"   && type === "spot")    return lbankSpotData;
  if (exchange === "lbank"   && type === "futures") return lbankFuturesData;
  if (exchange === "binance" && type === "spot")    return binanceSpotData;
  if (exchange === "binance" && type === "futures") return binanceFuturesData;
  if (exchange === "ourbit"  && type === "spot")    return ourbitSpotData;
  if (exchange === "ourbit"  && type === "futures") return ourbitFuturesData;

  return { isConnected:false, error:null, orderBook:{ asks:[], bids:[] }, tickerData:null };
};

const SUPPORTED_EXCHANGES = {
  mexc:   { title: "MEXC",    types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}_USDT`} } },
  gate:   { title: "Gate.io", types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}_USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}_USDT`} } },
  bingx:  { title: "BingX",   types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}-USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}-USDT`} } },
  bitget: { title: "Bitget",  types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDT`} } },
  bybit:  { title: "Bybit",   types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDT`} } },
  kucoin: { title: "KuCoin",  types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}-USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDTM`} } },
  binance:{ title: "Binance", types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDT`} } },
  lbank:  { title: "LBank",   types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDT`} } },
  ourbit: { title: "Ourbit",  types: { spot:{title:"SPOT",    symbolFormat:(s)=>`${s}USDT`},
                                       futures:{title:"FUTURES", symbolFormat:(s)=>`${s}USDT`} } },
};

// ---------- helpers: URL <-> state ----------
function safeType(t) { return t === "futures" ? "futures" : "spot"; }
function safeExchange(x) { return SUPPORTED_EXCHANGES[x] ? x : "bingx"; }
function fmtUrlKey(symbol, leftExch, leftType, rightExch, rightType) {
  // используем нижнее подчёркивание между exchange и type
  const s = (symbol || "").toUpperCase().trim();
  return `${s}-${leftExch}_${leftType}-${rightExch}_${rightType}`;
}
function parseUrlKey(raw) {
  // ожидаем: SYMBOL-leftExch_type-rightExch_type
  if (!raw) return null;
  try {
    const [sym, leftStr, rightStr] = raw.split("-");
    const [le, lt] = (leftStr || "").split(/[_-]/);
    const [re, rt] = (rightStr || "").split(/[_-]/);
    const symbol      = (sym || "ETH").toUpperCase();
    const buyExchange = safeExchange(le);
    const buyType     = safeType(lt);
    const sellExchange= safeExchange(re);
    const sellType    = safeType(rt);
    return { symbol, buyExchange, buyType, sellExchange, sellType };
  } catch {
    return null;
  }
}

const SpreadHistoryPage = () => {
  const params = useParams();                 // route should be /pair-analysis/:config?
  const navigate = useNavigate();

  // -------- init from URL (fallback — текущие дефолты)
  const parsed = parseUrlKey(params?.config);
  const [inputSymbol, setInputSymbol] = useState(parsed?.symbol || "ETH");
  const [activeSymbol, setActiveSymbol] = useState(parsed?.symbol || "ETH");
  const [buyExchange, setBuyExchange] = useState(parsed?.buyExchange || "bingx");
  const [buyType, setBuyType] = useState(parsed?.buyType || "spot");
  const [sellExchange, setSellExchange] = useState(parsed?.sellExchange || "bingx");
  const [sellType, setSellType] = useState(parsed?.sellType || "futures");
  const inputRef = useRef(null);

  // если поменяли URL вручную/по ссылке — подхватить новые значения в стейт
  useEffect(() => {
    const p = parseUrlKey(params?.config);
    if (!p) return;
    const need =
      p.symbol !== activeSymbol ||
      p.buyExchange !== buyExchange || p.buyType !== buyType ||
      p.sellExchange !== sellExchange || p.sellType !== sellType;

    if (need) {
      console.log("[PairAnalysis] URL → state", p);
      setInputSymbol(p.symbol);
      setActiveSymbol(p.symbol);
      setBuyExchange(p.buyExchange);
      setBuyType(p.buyType);
      setSellExchange(p.sellExchange);
      setSellType(p.sellType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.config]);

  // синхронизируем state → URL
  useEffect(() => {
    const key = fmtUrlKey(activeSymbol, buyExchange, buyType, sellExchange, sellType);
    if (params?.config !== key) {
      console.log("[PairAnalysis] state → URL", { key });
      navigate(`/pair-analysis/${key}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSymbol, buyExchange, buyType, sellExchange, sellType]);

  // -------- input handlers
  const debouncedSetSymbol = useCallback(
    debounce((v) => { if ((v || "").length >= 2) setActiveSymbol(v); }, 600),
    []
  );
  const handleSymbolChange = (e) => {
    const v = e.target.value.toUpperCase().trim();
    setInputSymbol(v);
    debouncedSetSymbol(v);
  };
  const handleBlur = () => {
    if (inputSymbol.length >= 2) setActiveSymbol(inputSymbol);
    else setInputSymbol(activeSymbol);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
      if (inputSymbol.length >= 2) setActiveSymbol(inputSymbol);
      else setInputSymbol(activeSymbol);
    }
  };

  // -------- symbols per exchange/type
  const buySymbol = useMemo(
    () => SUPPORTED_EXCHANGES[buyExchange]?.types[buyType]?.symbolFormat(activeSymbol),
    [buyExchange, buyType, activeSymbol]
  );
  const sellSymbol = useMemo(
    () => SUPPORTED_EXCHANGES[sellExchange]?.types[sellType]?.symbolFormat(activeSymbol),
    [sellExchange, sellType, activeSymbol]
  );

  const buyData  = useExchangeData(buyExchange,  buyType,  buySymbol);
  const sellData = useExchangeData(sellExchange, sellType, sellSymbol);

  const calcSpread = (buyPrice, sellPrice) =>
    (!buyPrice || !sellPrice) ? 0 : (((sellPrice - buyPrice) / buyPrice) * 100).toFixed(4);

  const buyPrice  = parseFloat(buyData?.tickerData?.lastPrice || 0);
  const sellPrice = parseFloat(sellData?.tickerData?.lastPrice || 0);
  const spreadIn  = calcSpread(buyPrice, sellPrice);
  const spreadOut = calcSpread(sellPrice, buyPrice);

  return (
    <div className="pair-analysis-page">
      <div className="control-panel">
        <div className="input-group">
          <label>Symbol:</label>
          <input
            ref={inputRef}
            type="text"
            value={inputSymbol}
            onChange={handleSymbolChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="BTC"
          />
        </div>

        <div className="exchange-selectors">
          <div className="buy-selectors">
            <select value={buyExchange} onChange={(e)=>setBuyExchange(e.target.value)}>
              {Object.entries(SUPPORTED_EXCHANGES).map(([k,x]) => <option key={k} value={k}>{x.title}</option>)}
            </select>
            <select value={buyType} onChange={(e)=>setBuyType(e.target.value)}>
              {Object.entries(SUPPORTED_EXCHANGES[buyExchange]?.types||{}).map(([k,t]) => <option key={k} value={k}>{t.title}</option>)}
            </select>
          </div>

          <div className="sell-selectors">
            <select value={sellExchange} onChange={(e)=>setSellExchange(e.target.value)}>
              {Object.entries(SUPPORTED_EXCHANGES).map(([k,x]) => <option key={k} value={k}>{x.title}</option>)}
            </select>
            <select value={sellType} onChange={(e)=>setSellType(e.target.value)}>
              {Object.entries(SUPPORTED_EXCHANGES[sellExchange]?.types||{}).map(([k,t]) => <option key={k} value={k}>{t.title}</option>)}
            </select>
          </div>
        </div>

      </div>

      <div className="pair-analysis-grid">
        

        <div>
          <SpreadChart
            symbol={activeSymbol}
            left={{  exchange: buyExchange,  type: buyType  }}
            right={{ exchange: sellExchange, type: sellType }}
            interval="1m"
          />
        </div>

        <InfoBlock
          buyOrderBook={buyData.orderBook}
          sellOrderBook={sellData.orderBook}
          exitBuyOrderBook={buyData.orderBook}
          exitSellOrderBook={sellData.orderBook}
          spreadIn={spreadIn}
          spreadOut={spreadOut}
          buyExchange={buyExchange}
          buyType={buyType}
          sellExchange={sellExchange}
          sellType={sellType}
          symbol={activeSymbol}
        />

        <div className="left-block">
          <TradingBlock
            symbol={buySymbol}
            exchange={buyExchange}
            type={buyType}
            baseSymbol={activeSymbol}
            {...buyData}
          />
        </div>

        <div className="right-block">
          <TradingBlock
            symbol={sellSymbol}
            exchange={sellExchange}
            type={sellType}
            baseSymbol={activeSymbol}
            {...sellData}
          />
        </div>

        
      </div>
    </div>
  );
};

export default SpreadHistoryPage;
